import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2, ArrowRight, Clock, Calendar, Users, AlertCircle, ChevronDown, UserPlus, Info, CheckCircle } from 'lucide-react';
export const WorkflowCreationModal = ({
  isOpen,
  onClose,
  departments,
  existingWorkflow
}) => {
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(departments[0]);
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [steps, setSteps] = useState([{
    name: 'Document Creation',
    assignee: '',
    description: ''
  }, {
    name: 'Department Review',
    assignee: '',
    description: ''
  }]);
  const [participants, setParticipants] = useState([]);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [newParticipantName, setNewParticipantName] = useState('');
  const [newParticipantEmail, setNewParticipantEmail] = useState('');
  const [newParticipantRole, setNewParticipantRole] = useState('Reviewer');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  useEffect(() => {
    if (existingWorkflow) {
      setWorkflowName(existingWorkflow.name);
      setWorkflowDescription(existingWorkflow.description);
      setSelectedDepartment(existingWorkflow.department || departments[0]);
      setDueDate(existingWorkflow.dueDate || '');
      setPriority(existingWorkflow.priority || 'Medium');
      setParticipants(existingWorkflow.participants || []);
      // Map existing workflow steps to our format
      if (existingWorkflow.steps && existingWorkflow.steps.length > 0) {
        const mappedSteps = existingWorkflow.steps.map(step => ({
          name: step.name,
          assignee: step.assignee || '',
          description: step.description || ''
        }));
        setSteps(mappedSteps);
      }
    }
  }, [existingWorkflow, departments]);
  const handleAddStep = () => {
    setSteps([...steps, {
      name: '',
      assignee: '',
      description: ''
    }]);
  };
  const handleRemoveStep = index => {
    const newSteps = [...steps];
    newSteps.splice(index, 1);
    setSteps(newSteps);
  };
  const handleStepChange = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index][field] = value;
    setSteps(newSteps);
  };
  const handleAddParticipant = () => {
    if (!newParticipantName.trim()) return;
    const initials = newParticipantName.split(' ').map(name => name[0]).join('').toUpperCase();
    const newParticipant = {
      name: newParticipantName,
      email: newParticipantEmail,
      role: newParticipantRole,
      initials
    };
    setParticipants([...participants, newParticipant]);
    setNewParticipantName('');
    setNewParticipantEmail('');
    setNewParticipantRole('Reviewer');
    setShowParticipantModal(false);
  };
  const handleRemoveParticipant = index => {
    const newParticipants = [...participants];
    newParticipants.splice(index, 1);
    setParticipants(newParticipants);
  };
  const validateForm = () => {
    const newErrors = {};
    if (!workflowName.trim()) {
      newErrors.name = 'Workflow name is required';
    }
    if (steps.some(step => !step.name.trim())) {
      newErrors.steps = 'All steps must have a name';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    // Create a new workflow object
    const workflow = {
      id: existingWorkflow ? existingWorkflow.id : Date.now(),
      name: workflowName,
      description: workflowDescription,
      department: selectedDepartment,
      status: existingWorkflow ? existingWorkflow.status : 'In Progress',
      startDate: existingWorkflow ? existingWorkflow.startDate : new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      dueDate,
      priority,
      participants,
      steps: steps.map((step, index) => ({
        name: step.name,
        assignee: step.assignee,
        description: step.description,
        status: existingWorkflow && existingWorkflow.steps[index] ? existingWorkflow.steps[index].status : index === 0 ? 'completed' : index === 1 ? 'current' : 'pending'
      }))
    };
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      onClose(workflow);
    }, 1000);
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-id="element-1464">
      <div className="bg-white w-full max-w-4xl h-[90vh] rounded-lg flex flex-col overflow-hidden" data-id="element-1465">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-1466">
          <h2 className="text-lg font-semibold text-gray-800" data-id="element-1467">
            {existingWorkflow ? 'Edit Workflow' : 'Create New Workflow'}
          </h2>
          <button className="text-gray-500 hover:text-gray-700" onClick={() => onClose(null)} data-id="element-1468">
            <X className="h-5 w-5" data-id="element-1469" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6" data-id="element-1470">
          <div className="space-y-6" data-id="element-1471">
            {Object.keys(errors).length > 0 && <div className="bg-red-50 border border-red-200 rounded-md p-4" data-id="element-1472">
                <div className="flex" data-id="element-1473">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" data-id="element-1474" />
                  <div data-id="element-1475">
                    <h3 className="text-sm font-medium text-red-800" data-id="element-1476">
                      Please correct the following errors:
                    </h3>
                    <ul className="mt-1 text-sm text-red-700 list-disc list-inside" data-id="element-1477">
                      {Object.values(errors).map((error, index) => <li key={index} data-id="element-1478">{error}</li>)}
                    </ul>
                  </div>
                </div>
              </div>}
            <div className="grid grid-cols-2 gap-6" data-id="element-1479">
              <div data-id="element-1480">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1481">
                  Workflow Name*
                </label>
                <input type="text" className={`w-full border ${errors.name ? 'border-red-300' : 'border-gray-300'} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`} placeholder="Enter workflow name" value={workflowName} onChange={e => setWorkflowName(e.target.value)} data-id="element-1482" />
              </div>
              <div data-id="element-1483">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1484">
                  Department
                </label>
                <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedDepartment} onChange={e => setSelectedDepartment(e.target.value)} data-id="element-1485">
                  {departments.map(dept => <option key={dept} value={dept} data-id="element-1486">
                      {dept}
                    </option>)}
                </select>
              </div>
            </div>
            <div data-id="element-1487">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1488">
                Description
              </label>
              <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 h-20" placeholder="Enter workflow description" value={workflowDescription} onChange={e => setWorkflowDescription(e.target.value)} data-id="element-1489"></textarea>
            </div>
            <div className="grid grid-cols-2 gap-6" data-id="element-1490">
              <div data-id="element-1491">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1492">
                  Due Date
                </label>
                <div className="relative" data-id="element-1493">
                  <input type="date" className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={dueDate} onChange={e => setDueDate(e.target.value)} data-id="element-1494" />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" data-id="element-1495" />
                </div>
              </div>
              <div data-id="element-1496">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1497">
                  Priority
                </label>
                <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={priority} onChange={e => setPriority(e.target.value)} data-id="element-1498">
                  <option value="Low" data-id="element-1499">Low</option>
                  <option value="Medium" data-id="element-1500">Medium</option>
                  <option value="High" data-id="element-1501">High</option>
                  <option value="Urgent" data-id="element-1502">Urgent</option>
                </select>
              </div>
            </div>
            <div data-id="element-1503">
              <div className="flex justify-between items-center mb-3" data-id="element-1504">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1505">
                  Participants
                </label>
                <button className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800" onClick={() => setShowParticipantModal(true)} data-id="element-1506">
                  <UserPlus className="h-4 w-4" data-id="element-1507" />
                  <span data-id="element-1508">Add Participant</span>
                </button>
              </div>
              {participants.length === 0 ? <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-center text-gray-500" data-id="element-1509">
                  No participants added yet. Click "Add Participant" to add
                  workflow participants.
                </div> : <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-id="element-1510">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-1511">
                    <thead className="bg-gray-50" data-id="element-1512">
                      <tr data-id="element-1513">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1514">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1515">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1516">
                          Role
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1517">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-1518">
                      {participants.map((participant, index) => <tr key={index} data-id="element-1519">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-1520">
                            <div className="flex items-center" data-id="element-1521">
                              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800 mr-3" data-id="element-1522">
                                {participant.initials}
                              </div>
                              <span className="text-sm text-gray-900" data-id="element-1523">
                                {participant.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1524">
                            {participant.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1525">
                            {participant.role}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1526">
                            <button className="text-red-600 hover:text-red-900" onClick={() => handleRemoveParticipant(index)} data-id="element-1527">
                              <Trash2 className="h-4 w-4" data-id="element-1528" />
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </div>
            <div data-id="element-1529">
              <div className="flex justify-between items-center mb-3" data-id="element-1530">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1531">
                  Workflow Steps*
                </label>
                <button className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800" onClick={handleAddStep} data-id="element-1532">
                  <Plus className="h-4 w-4" data-id="element-1533" />
                  <span data-id="element-1534">Add Step</span>
                </button>
              </div>
              <div className={`space-y-4 ${errors.steps ? 'border border-red-300 rounded-md p-4' : ''}`} data-id="element-1535">
                {steps.map((step, index) => <div key={index} className="bg-white border border-gray-200 rounded-md p-4" data-id="element-1536">
                    <div className="flex items-start justify-between mb-4" data-id="element-1537">
                      <div className="flex items-center" data-id="element-1538">
                        <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 mr-3" data-id="element-1539">
                          {index + 1}
                        </div>
                        <span className="text-sm font-medium text-gray-700" data-id="element-1540">
                          Step {index + 1}
                        </span>
                      </div>
                      {steps.length > 2 && <button className="text-red-600 hover:text-red-900" onClick={() => handleRemoveStep(index)} data-id="element-1541">
                          <Trash2 className="h-4 w-4" data-id="element-1542" />
                        </button>}
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1543">
                      <div data-id="element-1544">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1545">
                          Step Name*
                        </label>
                        <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter step name" value={step.name} onChange={e => handleStepChange(index, 'name', e.target.value)} data-id="element-1546" />
                      </div>
                      <div data-id="element-1547">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1548">
                          Assignee
                        </label>
                        <select className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={step.assignee} onChange={e => handleStepChange(index, 'assignee', e.target.value)} data-id="element-1549">
                          <option value="" data-id="element-1550">Select assignee</option>
                          {participants.map((participant, i) => <option key={i} value={participant.name} data-id="element-1551">
                              {participant.name}
                            </option>)}
                        </select>
                      </div>
                    </div>
                    <div data-id="element-1552">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1553">
                        Description
                      </label>
                      <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter step description" value={step.description} onChange={e => handleStepChange(index, 'description', e.target.value)} data-id="element-1554" />
                    </div>
                    {index < steps.length - 1 && <div className="flex justify-center my-2" data-id="element-1555">
                        <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-1556" />
                      </div>}
                  </div>)}
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4" data-id="element-1557">
              <div className="flex" data-id="element-1558">
                <Info className="h-5 w-5 text-blue-500 mr-2" data-id="element-1559" />
                <div data-id="element-1560">
                  <h3 className="text-sm font-medium text-blue-800" data-id="element-1561">
                    About Workflow Steps
                  </h3>
                  <p className="text-sm text-blue-700 mt-1" data-id="element-1562">
                    Define the steps required to complete this workflow. Each
                    step can be assigned to a participant. The workflow will
                    progress through these steps in order.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between" data-id="element-1563">
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" onClick={() => onClose(null)} data-id="element-1564">
            Cancel
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2" onClick={handleSubmit} disabled={isSubmitting} data-id="element-1565">
            {isSubmitting ? <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-1566"></div>
                <span data-id="element-1567">Saving...</span>
              </> : <>
                <CheckCircle className="h-4 w-4" data-id="element-1568" />
                <span data-id="element-1569">
                  {existingWorkflow ? 'Update Workflow' : 'Create Workflow'}
                </span>
              </>}
          </button>
        </div>
      </div>
      {/* Add Participant Modal */}
      {showParticipantModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-id="element-1570">
          <div className="bg-white w-full max-w-md rounded-lg shadow-xl overflow-hidden" data-id="element-1571">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-1572">
              <h3 className="text-lg font-semibold text-gray-800" data-id="element-1573">
                Add Participant
              </h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowParticipantModal(false)} data-id="element-1574">
                <X className="h-5 w-5" data-id="element-1575" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-1576">
              <div data-id="element-1577">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1578">
                  Name*
                </label>
                <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter participant name" value={newParticipantName} onChange={e => setNewParticipantName(e.target.value)} data-id="element-1579" />
              </div>
              <div data-id="element-1580">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1581">
                  Email
                </label>
                <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter participant email" value={newParticipantEmail} onChange={e => setNewParticipantEmail(e.target.value)} data-id="element-1582" />
              </div>
              <div data-id="element-1583">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1584">
                  Role
                </label>
                <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={newParticipantRole} onChange={e => setNewParticipantRole(e.target.value)} data-id="element-1585">
                  <option value="Reviewer" data-id="element-1586">Reviewer</option>
                  <option value="Approver" data-id="element-1587">Approver</option>
                  <option value="Observer" data-id="element-1588">Observer</option>
                  <option value="Admin" data-id="element-1589">Admin</option>
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1590">
              <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" onClick={() => setShowParticipantModal(false)} data-id="element-1591">
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" onClick={handleAddParticipant} disabled={!newParticipantName.trim()} data-id="element-1592">
                Add Participant
              </button>
            </div>
          </div>
        </div>}
    </div>;
};